import axios from 'axios';
import { attachAuthInterceptor } from './authToken';

const BASE_URL = process.env.REACT_APP_CLUBHOUSE_API_URL || 'http://localhost:5000';

const apiClient = axios.create({
  baseURL: `${BASE_URL}/api`,
});
attachAuthInterceptor(apiClient);

/** The caller's identity as resolved by the API from its bearer token. */
export interface MeResponse {
  /** Stable, storage-safe id that namespaces the user's data. */
  userId: string;
  name?: string | null;
  email?: string | null;
  picture?: string | null;
}

export interface GoogleExchangeUser {
  userId: string;
  name?: string | null;
  email?: string | null;
  picture?: string | null;
  provider: string;
}

export interface GoogleExchangeResponse {
  accessToken: string;
  expiresAt: string;
  user: GoogleExchangeUser;
}

export const authApi = {
  /** Exchanges a validated Entra External ID ID token for a first-party API session. */
  exchangeEntraCredential: async (idToken: string): Promise<GoogleExchangeResponse> => {
    const { data } = await apiClient.post<GoogleExchangeResponse>('/auth/entra/exchange', undefined, {
      headers: { Authorization: `Bearer ${idToken}` },
    });
    return data;
  },
  /** Exchanges a short-lived Google credential for a first-party API session. */
  exchangeGoogleCredential: async (idToken: string): Promise<GoogleExchangeResponse> => {
    const { data } = await apiClient.post<GoogleExchangeResponse>('/auth/google/exchange', { idToken });
    return data;
  },
  /** Exchanges a personal Microsoft account ID token for a first-party API session. */
  exchangeMicrosoftCredential: async (idToken: string): Promise<GoogleExchangeResponse> => {
    const { data } = await apiClient.post<GoogleExchangeResponse>('/auth/microsoft/exchange', { idToken });
    return data;
  },
  /** Exchanges a short-lived Facebook user token for a first-party API session. */
  exchangeFacebookCredential: async (accessToken: string): Promise<GoogleExchangeResponse> => {
    const { data } = await apiClient.post<GoogleExchangeResponse>('/auth/facebook/exchange', { accessToken });
    return data;
  },
  /** Exchanges a nonce-bound Apple identity token for a first-party API session. */
  exchangeAppleCredential: async (
    identityToken: string,
    nonce: string,
    displayName?: string,
  ): Promise<GoogleExchangeResponse> => {
    const { data } = await apiClient.post<GoogleExchangeResponse>('/auth/apple/exchange', {
      identityToken,
      nonce,
      displayName,
    });
    return data;
  },
  /** Exchanges a short-lived native-app handoff for a normal browser session. */
  exchangeClubhouseHandoff: async (token: string): Promise<GoogleExchangeResponse> => {
    const { data } = await apiClient.post<GoogleExchangeResponse>('/auth/clubhouse-handoff', { token });
    return data;
  },
  /** Links a Google identity to the currently authenticated account. */
  linkGoogleCredential: async (idToken: string): Promise<void> => {
    await apiClient.post('/auth/google/link', { idToken });
  },
  /** Links a Facebook identity to the currently authenticated account. */
  linkFacebookCredential: async (accessToken: string): Promise<void> => {
    await apiClient.post('/auth/facebook/link', { accessToken });
  },
  /** Links an Apple identity to the currently authenticated account. */
  linkAppleCredential: async (identityToken: string, nonce: string, displayName?: string): Promise<void> => {
    await apiClient.post('/auth/apple/link', { identityToken, nonce, displayName });
  },
  /** Resolves the signed-in user's canonical id and profile claims. */
  me: async (): Promise<MeResponse> => {
    const { data } = await apiClient.get<MeResponse>('/auth/me');
    return data;
  },
};
